import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertTerminalBody, SearchTerminal, Terminal, UpdateTerminalBody } from "./types/terminals";

export const useTerminalsApi = () => {
  const api = useApi();

  const useAdminTerminals = useCallback(
    (query: PaginationQuery, signal: AbortSignal, systemId?: string) => {
      const url = buildUrl(
        systemId ? `/admin/settings/patrol/systems/${systemId}/terminals` : `/admin/settings/patrol/terminals`,
        query,
      );
      return useSWR<Paginated<SearchTerminal>, CustomError, string>(url, (url) =>
        api.get<Paginated<SearchTerminal>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminTerminal = useCallback(
    async (body: InsertTerminalBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/terminals`;
      const id = await api.post<{ id: string }, InsertTerminalBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const getAdminTerminal = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/terminals`;
      return useSWRMutation<Terminal, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<Terminal>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const updateAdminTerminal = useCallback(
    async (terminalId: string, body: UpdateTerminalBody): Promise<void> => {
      const url = `/admin/settings/patrol/terminals/${terminalId}`;
      (await api.put<UpdateTerminalBody>(url, body), mutateCache([`/admin/settings/patrol/terminals`]));
    },
    [api],
  );

  const updateAdminTerminalVisibility = useCallback(
    async (terminalId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/terminals/${terminalId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/terminals"]);
    },
    [api],
  );

  const deleteAdminTerminal = useCallback(
    async (terminalId: string): Promise<void> => {
      const url = `/admin/settings/patrol/terminals/${terminalId}`;
      await api.del(url);
      mutateCache([`/admin/settings/patrol/terminals`, url]);
    },
    [api],
  );

  return {
    useAdminTerminals,
    insertAdminTerminal,
    getAdminTerminal,
    updateAdminTerminal,
    updateAdminTerminalVisibility,
    deleteAdminTerminal,
  };
};
